import React from 'react';
import { DeviceItem, NodeId } from './types';

interface DeviceRowProps {
  device: DeviceItem;
  selected: boolean;
  parentLabName?: string;
  renderMeta?: (device: DeviceItem) => React.ReactNode;
  onToggle: (id: NodeId) => void;
}

const DeviceRow: React.FC<DeviceRowProps> = ({
  device,
  selected,
  parentLabName,
  renderMeta,
  onToggle,
}) => {
  const inputId = `device-${device.id}`;
  return (
    <li
      role="option"
      aria-selected={selected}
      className={`device-selector__device-row ${selected ? 'is-selected' : ''}`}
    >
      <label htmlFor={inputId} className="d-flex align-items-center w-100 mb-0 px-3 py-2">
        <input
          id={inputId}
          type="checkbox"
          className="form-check-input me-2"
          checked={selected}
          onChange={() => onToggle(device.id)}
        />
        <span className="text-truncate flex-grow-1" title={device.name}>
          {device.name}
        </span>
        {renderMeta ? (
          <span className="ms-2 small text-muted">{renderMeta(device)}</span>
        ) : (
          parentLabName && (
            <span className="ms-2 small text-muted text-truncate" title={parentLabName}>
              {parentLabName}
            </span>
          )
        )}
      </label>
    </li>
  );
};

export interface DeviceListProps {
  focusedNodeName: string;
  visibleDevices: DeviceItem[];
  selectedIds: Set<NodeId>;
  parentLabNameById: Map<NodeId, string>;
  searchActive: boolean;
  onToggle: (id: NodeId) => void;
  onSelectAllVisible: (selectAll: boolean) => void;
  renderMeta?: (device: DeviceItem) => React.ReactNode;
}

export const DeviceList: React.FC<DeviceListProps> = ({
  focusedNodeName,
  visibleDevices,
  selectedIds,
  parentLabNameById,
  searchActive,
  onToggle,
  onSelectAllVisible,
  renderMeta,
}) => {
  const visibleSelectedCount = visibleDevices.filter((d) => selectedIds.has(d.id)).length;
  const allSelected =
    visibleDevices.length > 0 && visibleSelectedCount === visibleDevices.length;
  const isIndeterminate =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleDevices.length;

  return (
    <div className="device-selector__device-list">
      <div className="device-selector__device-list-header d-flex align-items-center px-3 py-2">
        <div className="text-truncate" title={focusedNodeName}>
          <strong>{focusedNodeName}</strong>
          <span className="text-muted ms-2">· {visibleDevices.length} devices</span>
        </div>
        {visibleDevices.length > 0 && (
          <div className="ms-auto form-check mb-0">
            <input
              id="device-list-select-all"
              className="form-check-input"
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate;
              }}
              onChange={() => onSelectAllVisible(!allSelected)}
            />
            <label className="form-check-label" htmlFor="device-list-select-all">
              Select all
            </label>
          </div>
        )}
      </div>
      {visibleDevices.length === 0 ? (
        <div className="text-center text-muted p-4">
          {searchActive ? 'No devices match your search.' : 'No devices in this scope.'}
        </div>
      ) : (
        <ul
          className="list-unstyled mb-0"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Devices"
        >
          {visibleDevices.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              selected={selectedIds.has(d.id)}
              parentLabName={parentLabNameById.get(d.parentId)}
              renderMeta={renderMeta}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
